import { BarChart3, ClipboardList, LogOut, Megaphone, Menu as MenuIcon, MessageSquare, Moon, Settings, Sun, UtensilsCrossed, Users, FileText, Wifi, WifiOff } from 'lucide-react';
import { BRAND_LOGO, BRAND_NAME } from '@/lib/brand';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useAuth } from '@/lib/auth';
import { useRealtime } from '@/lib/realtime';
import { applyTheme, storedTheme } from '@/lib/platform';
import { cn, initials } from '@/lib/utils';
import { Tooltip } from '@/components/ui/misc';
import { DesktopDownload } from './DesktopDownload';

const NAV = [
  { to: '/inbox', label: 'Inbox', icon: MessageSquare },
  { to: '/orders', label: 'Orders', icon: ClipboardList },
  { to: '/menu', label: 'Menu', icon: UtensilsCrossed },
  { to: '/templates', label: 'Templates', icon: FileText },
  { to: '/promotions', label: 'Promotions', icon: Megaphone, owner: true },
  { to: '/dashboard', label: 'Dashboard', icon: BarChart3, owner: true },
  { to: '/settings', label: 'Settings', icon: Settings, owner: true },
  { to: '/users', label: 'Users', icon: Users, owner: true },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const { connected } = useRealtime();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(storedTheme());
  const items = NAV.filter((n) => !n.owner || user?.role === 'owner');

  const toggleTheme = () => {
    const next = document.documentElement.classList.contains('dark') ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem('theme', next);
    } catch {
      /* private mode */
    }
    applyTheme(next);
  };

  const nav = (
    <nav className="flex flex-1 flex-col gap-1">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
              isActive && 'bg-accent text-accent-foreground',
            )
          }
        >
          <Icon className="size-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );

  const footer = (
    <div className="flex items-center gap-2 border-t pt-3">
      <div className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{initials(user?.name)}</div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{user?.name}</p>
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          {connected ? <Wifi className="size-3 text-success" /> : <WifiOff className="size-3 text-destructive" />}
          {connected ? 'Live' : 'Reconnecting…'} · {user?.role}
        </p>
      </div>
      <Tooltip content={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
        <button onClick={toggleTheme} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Toggle theme">
          {document.documentElement.classList.contains('dark') ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
      </Tooltip>
      <Tooltip content="Log out">
        <button onClick={() => void logout()} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Log out">
          <LogOut className="size-4" />
        </button>
      </Tooltip>
    </div>
  );

  return (
    <div className="flex h-full">
      <aside className="no-print hidden w-60 shrink-0 flex-col gap-4 border-r bg-card p-3 md:flex">
        <Brand />
        {nav}
        <DesktopDownload className="px-3" />
        {footer}
      </aside>
      {open ? (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col gap-4 bg-card p-3" onClick={(e) => e.stopPropagation()}>
            <Brand />
            {nav}
            {footer}
          </aside>
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print flex h-12 items-center gap-2 border-b bg-card px-3 md:hidden">
          <button onClick={() => setOpen(true)} className="rounded-md p-1.5 hover:bg-accent" aria-label="Open menu">
            <MenuIcon className="size-5" />
          </button>
          <Brand compact />
        </header>
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  return (
    <div className={cn('flex items-center gap-2 px-2', !compact && 'py-1')}>
      {BRAND_LOGO && <img src={BRAND_LOGO} alt="" className="h-10 w-auto shrink-0 object-contain dark:invert" />}
      <span className="font-semibold tracking-tight">{BRAND_NAME}</span>
    </div>
  );
}
